import type {
  AcademyOperatingProfileInput,
  ClubOperatingProfileInput,
  OrgOperationType,
  SubscriptionTier,
  ClubPlanTier,
} from '@court-sync/shared';

export const ONBOARDING_DRAFT_KEY = 'courtsync_onboarding_draft';

export interface OnboardingDraft {
  orgOperationType?: OrgOperationType;
  operatingProfile: Partial<AcademyOperatingProfileInput>;
  clubOperatingProfile?: Partial<ClubOperatingProfileInput>;
  recommendedTier?: SubscriptionTier;
  selectedPlanTier?: SubscriptionTier;
  /** Set when the operator explicitly picks a plan on the onboarding plan step. */
  planTierManuallySelected?: boolean;
  recommendedClubTier?: ClubPlanTier;
  selectedClubPlanTier?: ClubPlanTier;
  clubPlanTierManuallySelected?: boolean;
  academyName?: string;
  clubName?: string;
  country?: string;
  stateOrProvince?: string;
  academyTimezone?: string;
  clubTimezone?: string;
  organizationId?: string;
  currentStepId?: string;
  completedStepIds?: string[];
  starterLimitationsAcknowledged?: boolean;
  /** When set, skip questionnaire and plan recommendation (pricing page fast track). */
  pricingFastTrack?: boolean;
  pricingSelectionSource?: 'pricing';
}

const EMPTY_DRAFT: OnboardingDraft = {
  operatingProfile: {},
  clubOperatingProfile: {},
  completedStepIds: [],
};

export function readOnboardingDraft(): OnboardingDraft {
  if (typeof window === 'undefined') return { ...EMPTY_DRAFT };
  try {
    const raw =
      localStorage.getItem(ONBOARDING_DRAFT_KEY) ?? sessionStorage.getItem(ONBOARDING_DRAFT_KEY);
    if (!raw) return { ...EMPTY_DRAFT };
    const parsed = JSON.parse(raw) as OnboardingDraft;
    return {
      ...EMPTY_DRAFT,
      ...parsed,
      operatingProfile: parsed.operatingProfile ?? {},
      clubOperatingProfile: parsed.clubOperatingProfile ?? {},
      completedStepIds: parsed.completedStepIds ?? [],
    };
  } catch {
    return { ...EMPTY_DRAFT };
  }
}

export function writeOnboardingDraft(draft: OnboardingDraft): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(ONBOARDING_DRAFT_KEY, JSON.stringify(draft));
  sessionStorage.removeItem(ONBOARDING_DRAFT_KEY);
}

export function mergeOnboardingDraft(partial: Partial<OnboardingDraft>): OnboardingDraft {
  const current = readOnboardingDraft();
  const next: OnboardingDraft = {
    ...current,
    ...partial,
    operatingProfile: {
      ...current.operatingProfile,
      ...partial.operatingProfile,
    },
    clubOperatingProfile: {
      ...current.clubOperatingProfile,
      ...partial.clubOperatingProfile,
    },
    completedStepIds: partial.completedStepIds ?? current.completedStepIds,
  };
  writeOnboardingDraft(next);
  return next;
}

export function clearOnboardingDraft(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ONBOARDING_DRAFT_KEY);
  sessionStorage.removeItem(ONBOARDING_DRAFT_KEY);
}

export function persistSelectedPlanTier(tier: SubscriptionTier): void {
  mergeOnboardingDraft({ selectedPlanTier: tier });
  sessionStorage.setItem('selectedAcademyPlanId', tier);
  sessionStorage.removeItem('selectedPlanId');
}

/** @deprecated Use persistSelectedPlanTier — selection must not overwrite questionnaire recommendation. */
export function persistRecommendedPlanTier(tier: SubscriptionTier): void {
  persistSelectedPlanTier(tier);
}

export function persistSelectedClubPlanTier(tier: ClubPlanTier): void {
  mergeOnboardingDraft({ selectedClubPlanTier: tier });
  sessionStorage.setItem('selectedClubPlanId', tier);
  sessionStorage.removeItem('selectedPlanId');
}
